import { useState, useEffect, useRef, type FC } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
// Use Box instead of Paper so the fields are not rendered as separate "cards"
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  BusinessCenter as BusinessIcon,
  Description as DescriptionIcon,
  Title as TitleIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ComponentInfoCardProps, ComponentInfoData } from './types';
import { useComponentInfoData } from './useComponentInfoData';
import { useSystemData } from './useSystemData';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}10 50%, ${theme.palette.background.paper} 100%)`,
  border: `1px solid ${theme.palette.primary.light}40`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.secondary.light}15 50%, ${theme.palette.background.paper} 100%)`,
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 0,
  padding: theme.spacing(1.25, 0),
  // remove card-like background/border so rows visually belong to the same container
  backgroundColor: 'transparent',
  borderRadius: 0,
  border: 'none',
  // subtle divider between rows
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 0.15s ease-in-out, transform 0.15s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const DetailContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0, // Allow text truncation
}));

const DetailLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '0.95rem',
  wordBreak: 'break-word',
}));

const EditableRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-input': {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    '& fieldset': {
      border: `2px solid ${theme.palette.divider}`,
    },
    '&:hover fieldset': {
      border: `2px solid ${theme.palette.primary.main}`,
    },
    '&.Mui-focused fieldset': {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.action.hover,
  width: 36,
  height: 36,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    transform: 'scale(1.1)',
  },
  '&.save': {
    color: theme.palette.success.main,
    backgroundColor: `${theme.palette.success.light}20`,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
  },
  '&.cancel': {
    color: theme.palette.error.main,
    backgroundColor: `${theme.palette.error.light}20`,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
  },
}));

// Info chip removed per design update - fields are still editable via inline controls

export const ComponentInfoCard: FC<ComponentInfoCardProps> = ({
  selectedRepository,
  data: sharedData,
  onDataUpdate,
}) => {
  const {
    data: hookData,
    loading,
    error,
  } = useComponentInfoData(selectedRepository);
  const {
    systems,
    loading: systemsLoading,
    error: systemsError,
  } = useSystemData();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const initializedRef = useRef(false);
  const lastRepositoryRef = useRef<string>('');

  // Use shared data if available, otherwise use hook data
  const data = sharedData || hookData;

  // Reset initialization flag when repository changes
  useEffect(() => {
    if (lastRepositoryRef.current !== selectedRepository) {
      initializedRef.current = false;
      lastRepositoryRef.current = selectedRepository;
    }
  }, [selectedRepository]);

  // Initialize shared state when hook data is loaded (only once per repository)
  useEffect(() => {
    if (hookData && !sharedData && onDataUpdate && !initializedRef.current) {
      initializedRef.current = true;
      onDataUpdate(hookData);
    }
  }, [hookData, sharedData, onDataUpdate]);

  // Don't render if no repository is selected
  if (!selectedRepository) {
    return null;
  }

  // Show loading state
  if (loading) {
    return (
      <Fade in timeout={600}>
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <CardHeader>
              <InfoIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600} color="primary">
                Component Information
              </Typography>
            </CardHeader>
            <SectionContainer>
              {[1, 2, 3, 4].map(i => (
                <DetailRow key={i}>
                  <DetailContent>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="text" width="60%" height={24} />
                  </DetailContent>
                  <Skeleton variant="circular" width={28} height={28} />
                </DetailRow>
              ))}
            </SectionContainer>
          </CardContent>
        </StyledCard>
      </Fade>
    );
  }

  // Show error state
  if (error) {
    return (
      <Fade in timeout={600}>
        <StyledCard>
          <CardContent sx={{ p: 3 }}>
            <CardHeader>
              <InfoIcon color="primary" sx={{ fontSize: 28 }} />
              <Typography variant="h5" fontWeight={600} color="primary">
                Component Information
              </Typography>
            </CardHeader>
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          </CardContent>
        </StyledCard>
      </Fade>
    );
  }

  // Don't render if no data
  if (!data) {
    return null;
  }

  const handleEditStart = (fieldName: string, currentValue: string) => {
    setEditValues({ ...editValues, [fieldName]: currentValue });
    setEditingField(fieldName);
  };

  const handleEditSave = (fieldName: string) => {
    if (data && onDataUpdate) {
      const updatedData: ComponentInfoData = {
        ...data,
        [fieldName]:
          editValues[fieldName] || data[fieldName as keyof ComponentInfoData],
      };
      onDataUpdate(updatedData);
    }
    setEditingField(null);
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValues({});
  };

  const renderTenantDropdownField = (
    fieldName: string,
    label: string,
    value: string,
    icon: React.ReactNode,
  ) => {
    const isEditing = editingField === fieldName;
    const editValue = editValues[fieldName] || value;

    // Build Select children without nested ternary (eslint: no-nested-ternary)
    let tenantOptionsContent: React.ReactNode;
    if (systemsLoading) {
      tenantOptionsContent = (
        <MenuItem disabled>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Loading systems...
        </MenuItem>
      );
    } else if (systemsError) {
      tenantOptionsContent = (
        <MenuItem disabled>Error loading systems</MenuItem>
      );
    } else if (systems.length === 0) {
      tenantOptionsContent = (
        <MenuItem disabled>No systems found that you own</MenuItem>
      );
    } else {
      tenantOptionsContent = systems.map(system => (
        <MenuItem key={system.entityRef} value={system.name}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {system.title || system.name}
            </Typography>
            {system.description && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {system.description}
              </Typography>
            )}
          </Box>
        </MenuItem>
      ));
    }

    return (
      <DetailRow>
        <DetailContent>
          <DetailLabel>
            {icon}
            <Typography
              variant="subtitle2"
              color="textSecondary"
              fontWeight={600}
            >
              {label}
            </Typography>
          </DetailLabel>
          {isEditing ? (
            <EditableRow>
              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel>Select Tenant System</InputLabel>
                <Select
                  value={editValue}
                  onChange={e =>
                    setEditValues({
                      ...editValues,
                      [fieldName]: e.target.value,
                    })
                  }
                  label="Select Tenant System"
                  disabled={systemsLoading}
                >
                  {tenantOptionsContent}
                </Select>
              </FormControl>
              <ActionButton
                size="small"
                onClick={() => handleEditSave(fieldName)}
                className="save"
                title="Save changes"
              >
                <CheckIcon />
              </ActionButton>
              <ActionButton
                size="small"
                onClick={handleEditCancel}
                className="cancel"
                title="Cancel editing"
              >
                <CloseIcon />
              </ActionButton>
            </EditableRow>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DetailValue
                onClick={() => handleEditStart(fieldName, value)}
                sx={{ cursor: 'pointer', flex: 1 }}
              >
                {value}
              </DetailValue>
              <ActionButton
                size="small"
                onClick={() => handleEditStart(fieldName, value)}
                title={`Edit ${label.toLowerCase()}`}
              >
                <EditIcon />
              </ActionButton>
            </Box>
          )}
        </DetailContent>
      </DetailRow>
    );
  };

  const renderEditableField = (
    fieldName: string,
    label: string,
    value: string,
    icon: React.ReactNode,
    multiline = false,
    editable = true,
  ) => {
    const isEditing = editingField === fieldName;
    const editValue = editValues[fieldName] || value;

    return (
      <DetailRow>
        <DetailContent>
          <DetailLabel>
            {icon}
            <Typography
              variant="subtitle2"
              color="textSecondary"
              fontWeight={600}
            >
              {label}
            </Typography>
          </DetailLabel>
          {isEditing ? (
            <EditableRow>
              <StyledTextField
                size="small"
                value={editValue}
                onChange={e =>
                  setEditValues({ ...editValues, [fieldName]: e.target.value })
                }
                variant="outlined"
                multiline={multiline}
                rows={multiline ? 2 : 1}
                placeholder={`Enter ${label.toLowerCase()}...`}
              />
              <ActionButton
                size="small"
                onClick={() => handleEditSave(fieldName)}
                className="save"
                title="Save changes"
              >
                <CheckIcon />
              </ActionButton>
              <ActionButton
                size="small"
                onClick={handleEditCancel}
                className="cancel"
                title="Cancel editing"
              >
                <CloseIcon />
              </ActionButton>
            </EditableRow>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {editable ? (
                <>
                  <DetailValue
                    onClick={() => handleEditStart(fieldName, value)}
                    sx={{ cursor: 'pointer', flex: 1 }}
                  >
                    {value}
                  </DetailValue>
                  <ActionButton
                    size="small"
                    onClick={() => handleEditStart(fieldName, value)}
                    title={`Edit ${label.toLowerCase()}`}
                  >
                    <EditIcon />
                  </ActionButton>
                </>
              ) : (
                <DetailValue sx={{ flex: 1 }}>{value}</DetailValue>
              )}
            </Box>
          )}
        </DetailContent>
      </DetailRow>
    );
  };

  return (
    <Fade in timeout={600}>
      <StyledCard>
        <CardContent sx={{ p: 3 }}>
          <CardHeader>
            <InfoIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600} color="primary">
              Component Information
            </Typography>
          </CardHeader>

          <SectionContainer>
            {renderEditableField(
              'githubRepository',
              'Github Repository',
              data.githubRepository || '',
              <InfoIcon color="primary" sx={{ fontSize: 18 }} />,
              false, // not multiline
              false, // not editable
            )}
            {renderEditableField(
              'componentName',
              'Component Name',
              data.componentName,
              <BadgeIcon color="primary" sx={{ fontSize: 18 }} />,
            )}

            {renderEditableField(
              'componentTitle',
              'Component Title',
              data.componentTitle,
              <TitleIcon color="secondary" sx={{ fontSize: 18 }} />,
            )}

            {renderEditableField(
              'componentDescription',
              'Component Description',
              data.componentDescription,
              <DescriptionIcon color="info" sx={{ fontSize: 18 }} />,
              true, // multiline
            )}

            {renderTenantDropdownField(
              'tenant',
              'Tenant',
              data.tenant,
              <BusinessIcon color="warning" sx={{ fontSize: 18 }} />,
            )}
          </SectionContainer>
        </CardContent>
      </StyledCard>
    </Fade>
  );
};
